import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Play, Pause, XCircle, RefreshCw, Loader2, 
  Calendar, PoundSterling, User, Mail, AlertCircle, CheckCircle,
  Edit, Plus, RotateCcw, History, FileClock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger
} from '../ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../ui/dialog';
import { RowActionsMenu } from './RowActionsMenu';

import { API_URL } from '../../utils/api';

export function AdminPaymentPlansTab() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [processResults, setProcessResults] = useState(null);
  const [processingPlanId, setProcessingPlanId] = useState(null);
  const [recalculatingPlanId, setRecalculatingPlanId] = useState(null);
  
  // Manual payment recording state
  const [recordPaymentModalOpen, setRecordPaymentModalOpen] = useState(false);
  const [adjustBalanceModalOpen, setAdjustBalanceModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [adjustedBalance, setAdjustedBalance] = useState('');
  const [adjustedPaymentsMade, setAdjustedPaymentsMade] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Process payment confirmation state
  const [processConfirmOpen, setProcessConfirmOpen] = useState(false);
  const [planToProcess, setPlanToProcess] = useState(null);

  // Manual card charge (admin collect payment) state
  const [manualChargeOpen, setManualChargeOpen] = useState(false);
  const [manualBooking, setManualBooking] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [cardMerchant, setCardMerchant] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [loadingCards, setLoadingCards] = useState(false);
  const [charging, setCharging] = useState(false);
  const [chargeResult, setChargeResult] = useState(null);

  // Read-only manual-charge history state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyBooking, setHistoryBooking] = useState(null);
  const [historyCharges, setHistoryCharges] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Amend instalment amount (future instalments only) state
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendPlan, setAmendPlan] = useState(null);
  const [amendMonthly, setAmendMonthly] = useState('');
  const [amendEndDate, setAmendEndDate] = useState('');
  const [amendSaving, setAmendSaving] = useState(false);

  // Read-only instalment-amendment history state
  const [amendHistoryOpen, setAmendHistoryOpen] = useState(false);
  const [amendHistoryPlan, setAmendHistoryPlan] = useState(null);

  // Mark as Completed — Verified
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyPlan, setVerifyPlan] = useState(null);
  const [verifyNote, setVerifyNote] = useState('');
  const [verifyConfirmed, setVerifyConfirmed] = useState(false);
  const [verifySaving, setVerifySaving] = useState(false);

  // Reopen a Completed plan
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenPlan, setReopenPlan] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenSaving, setReopenSaving] = useState(false);
  const [amendHistoryRecords, setAmendHistoryRecords] = useState([]);
  const [amendHistoryLoading, setAmendHistoryLoading] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = `${API_URL}/api/weekends/admin/payment-plans`;
      console.log('[PaymentPlans] Fetching, API_URL:', API_URL, 'endpoint:', endpoint, 'host:', window.location.hostname);
      
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      } else {
        toast.error('Failed to load payment plans');
      }
    } catch (error) {
      toast.error('Error loading payment plans');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessNow = async () => {
    setProcessing(true);
    setProcessResults(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans/process-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      setProcessResults(result);
      
      if (response.ok) {
        if (result.error) {
          toast.error(result.error);
        } else if (result.due_count === 0) {
          toast.info(result.message);
        } else {
          toast.success(result.message);
          fetchPlans(); // Refresh the list
        }
      } else {
        toast.error(result.detail || result.message || 'Failed to process payment plans');
      }
    } catch (error) {
      console.error('Process payment plans error:', error);
      toast.error('Error connecting to server');
    } finally {
      setProcessing(false);
    }
  };

  // Open process payment confirmation dialog
  const openProcessConfirmation = (plan) => {
    setPlanToProcess(plan);
    setProcessConfirmOpen(true);
  };

  // Actually process the payment after confirmation
  const confirmProcessPayment = async () => {
    if (!planToProcess) return;
    setProcessConfirmOpen(false);
    const planId = planToProcess.plan_id || planToProcess.share_id;
    await handleProcessSinglePlan(planId);
    setPlanToProcess(null);
  };

  const handleProcessSinglePlan = async (planId) => {
    setProcessingPlanId(planId);
    try {
      const token = localStorage.getItem('token');
      const endpoint = `${API_URL}/api/weekends/admin/payment-plans/${planId}/process`;
      console.log('[PaymentPlans] Processing single plan, endpoint:', endpoint, 'planId:', planId);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('[PaymentPlans] Response status:', response.status, response.statusText);
      
      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // Response might not be JSON
          const text = await response.text().catch(() => '');
          if (text) errorMessage = text;
        }
        toast.error(errorMessage);
        fetchPlans(); // Refresh anyway
        return;
      }
      
      const result = await response.json();
      
      if (result.warning) {
        toast.warning(`Payment processed with warning: ${result.warning}`);
      } else {
        toast.success(`Payment of £${result.amount_charged?.toFixed(2)} processed! New balance: £${result.new_balance?.toFixed(2)}`);
      }
      fetchPlans(); // Refresh the list
    } catch (error) {
      console.error('Process single plan error:', error);
      toast.error(`Error: ${error.message || 'Failed to connect to server'}`);
      fetchPlans();
    } finally {
      setProcessingPlanId(null);
    }
  };

  // --- Manual card charge (admin collect payment) ---
  const openManualCharge = async (plan) => {
    const bookingId = plan.booking_id;
    setManualBooking({ booking_id: bookingId, customer_name: plan.customer_name, monthly_amount: plan.monthly_amount });
    setSavedCards([]);
    setSelectedCardId('');
    setManualAmount('');
    setManualNote('');
    setChargeResult(null);
    setCardMerchant(null);
    setManualChargeOpen(true);
    setLoadingCards(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/weekends/admin/bookings/${bookingId}/saved-cards`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || 'Could not load saved cards');
      } else {
        setSavedCards(data.cards || []);
        setCardMerchant(data.merchant || null);
        if ((data.cards || []).length === 1) setSelectedCardId(data.cards[0].card_id);
      }
    } catch (e) {
      toast.error('Error loading saved cards');
    } finally {
      setLoadingCards(false);
    }
  };

  const submitManualCharge = async () => {
    if (!manualBooking) return;
    const amountNum = parseFloat(manualAmount);
    if (!selectedCardId) { toast.error('Please select a card'); return; }
    if (!amountNum || amountNum <= 0) { toast.error('Enter a valid amount'); return; }
    // One-time confirmation token so duplicate clicks can never double-charge.
    const confirmationToken = (window.crypto?.randomUUID?.() || `tok-${Date.now()}-${Math.random().toString(16).slice(2)}`);
    setCharging(true);
    setChargeResult(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/weekends/admin/bookings/${manualBooking.booking_id}/charge-card`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          card_id: selectedCardId,
          amount: amountNum,
          note: manualNote || undefined,
          confirmation_token: confirmationToken
        })
      });
      const data = await resp.json();
      if (!resp.ok) {
        setChargeResult({ ok: false, detail: data.detail || `Error ${resp.status}` });
        toast.error(data.detail || 'Payment was not completed');
      } else {
        setChargeResult({ ok: true, payment_id: data.square_payment_id, amount: data.amount, last4: data.card_last4 });
        toast.success(`Payment of £${Number(data.amount).toFixed(2)} successful`);
      }
    } catch (e) {
      setChargeResult({ ok: false, detail: 'Could not reach the server. Verify in Square before retrying.' });
      toast.error('Network error - verify in Square before retrying');
    } finally {
      setCharging(false);
    }
  };

  const openHistory = async (plan) => {
    const bookingId = plan.booking_id;
    setHistoryBooking({ booking_id: bookingId, customer_name: plan.customer_name });
    setHistoryCharges([]);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/weekends/admin/bookings/${bookingId}/manual-charges`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || 'Could not load charge history');
      } else {
        setHistoryCharges(data.charges || []);
      }
    } catch (e) {
      toast.error('Error loading charge history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openAmendHistory = async (plan) => {
    setAmendHistoryPlan({ plan_id: plan.plan_id, customer_name: plan.customer_name });
    setAmendHistoryRecords([]);
    setAmendHistoryOpen(true);
    setAmendHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${plan.plan_id}/amendments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || 'Could not load amendment history');
      } else {
        setAmendHistoryRecords(data.amendments || []);
      }
    } catch (e) {
      toast.error('Error loading amendment history');
    } finally {
      setAmendHistoryLoading(false);
    }
  };


  const openAmend = (plan) => {
    setAmendPlan(plan);
    setAmendMonthly('');
    setAmendEndDate('');
    setAmendOpen(true);
  };

  const submitAmend = async () => {
    if (!amendPlan) return;
    const newMonthly = parseFloat(amendMonthly);
    if (!newMonthly || newMonthly <= 0) { toast.error('Enter a valid new monthly amount'); return; }
    setAmendSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { monthly_amount: newMonthly };
      if (amendEndDate) payload.end_date = amendEndDate;
      const resp = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${amendPlan.plan_id}/amend-instalment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || 'Could not amend instalment amount');
      } else {
        toast.success(data.message || 'Monthly instalment amount updated');
        setAmendOpen(false);
        fetchPlans();
      }
    } catch (e) {
      toast.error('Error amending instalment amount');
    } finally {
      setAmendSaving(false);
    }
  };



  const handlePausePlan = async (planId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${planId}/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Payment plan paused');
        fetchPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to pause plan');
      }
    } catch (error) {
      toast.error('Error pausing plan');
    }
  };

  const handleResumePlan = async (planId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${planId}/resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Payment plan resumed');
        fetchPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to resume plan');
      }
    } catch (error) {
      toast.error('Error resuming plan');
    }
  };

  const handleCancelPlan = async (planId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${planId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Payment plan cancelled');
        fetchPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel plan');
      }
    } catch (error) {
      toast.error('Error cancelling plan');
    }
  };

  const openVerifyComplete = (plan) => {
    setVerifyPlan(plan);
    setVerifyNote('');
    setVerifyConfirmed(false);
    setVerifyOpen(true);
  };

  const submitVerifyComplete = async () => {
    if (!verifyPlan || !verifyConfirmed) return;
    setVerifySaving(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${verifyPlan.plan_id}/mark-completed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ verified: true, note: verifyNote })
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || 'Could not mark plan Completed');
      } else {
        toast.success('Plan marked Completed — Verified');
        setVerifyOpen(false);
        fetchPlans();
      }
    } catch (e) {
      toast.error('Error marking plan Completed');
    } finally {
      setVerifySaving(false);
    }
  };

  const openReopen = (plan) => {
    setReopenPlan(plan);
    setReopenReason('');
    setReopenOpen(true);
  };

  const submitReopen = async () => {
    if (!reopenPlan) return;
    if (!reopenReason.trim()) { toast.error('Please enter a reason for reopening'); return; }
    setReopenSaving(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${reopenPlan.plan_id}/reopen`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reopenReason.trim() })
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || 'Could not reopen plan');
      } else {
        toast.success('Plan reopened as Active');
        setReopenOpen(false);
        fetchPlans();
      }
    } catch (e) {
      toast.error('Error reopening plan');
    } finally {
      setReopenSaving(false);
    }
  };

  // Open Record Payment modal
  const openRecordPaymentModal = (plan) => {
    setSelectedPlan(plan);
    setPaymentAmount('');
    setPaymentNote('');
    setRecordPaymentModalOpen(true);
  };

  // Open Adjust Balance modal
  const openAdjustBalanceModal = (plan) => {
    setSelectedPlan(plan);
    setAdjustedBalance(plan.remaining_balance?.toString() || '0');
    setAdjustedPaymentsMade(plan.payments_made?.toString() || '0');
    setAdjustNote('');
    setAdjustBalanceModalOpen(true);
  };

  // Record a manual payment
  const handleRecordPayment = async () => {
    if (!selectedPlan || !paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const planId = selectedPlan.plan_id;
      const endpoint = `${API_URL}/api/weekends/admin/payment-plans/${planId}/record-payment`;
      console.log('[PaymentPlans] Recording payment, planId:', planId, 'endpoint:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: parseFloat(paymentAmount),
          note: paymentNote || 'Manually recorded payment via Square dashboard'
        })
      });
      
      console.log('[PaymentPlans] Record payment response status:', response.status);
      
      if (!response.ok) {
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          const text = await response.text().catch(() => '');
          if (text) errorMessage = text;
        }
        toast.error(errorMessage);
        return;
      }
      
      const result = await response.json();
      toast.success(result.message || 'Payment recorded successfully');
      setRecordPaymentModalOpen(false);
      setPaymentAmount('');
      setPaymentNote('');
      fetchPlans();
    } catch (error) {
      console.error('Record payment error:', error);
      toast.error(`Error: ${error.message || 'Failed to record payment'}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Adjust balance directly
  const handleAdjustBalance = async () => {
    if (!selectedPlan) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${selectedPlan.plan_id}/adjust-balance`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          remaining_balance: parseFloat(adjustedBalance) || 0,
          payments_made: parseInt(adjustedPaymentsMade) || 0,
          note: adjustNote || 'Balance adjusted by admin'
        })
      });
      
      const result = await response.json();
      if (response.ok) {
        toast.success(result.message || 'Balance adjusted successfully');
        setAdjustBalanceModalOpen(false);
        fetchPlans();
      } else {
        toast.error(result.detail || 'Failed to adjust balance');
      }
    } catch (error) {
      console.error('Adjust balance error:', error);
      toast.error('Error adjusting balance');
    } finally {
      setSubmitting(false);
    }
  };

  // Recalculate from booking
  const handleRecalculateFromBooking = async (planId) => {
    setRecalculatingPlanId(planId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/weekends/admin/payment-plans/${planId}/recalculate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      if (response.ok) {
        toast.success(
          <div className="space-y-1">
            <p className="font-medium">Payment plan recalculated!</p>
            <p className="text-sm">
              Deposit: £{result.booking_deposit?.toFixed(2)} | 
              Total Paid: £{result.booking_amount_paid?.toFixed(2)}
            </p>
            <p className="text-sm">
              New Balance: £{result.new_remaining_balance?.toFixed(2)} | 
              Payments: {result.new_payments_made}
            </p>
          </div>
        );
        fetchPlans();
      } else {
        toast.error(result.detail || 'Failed to recalculate');
      }
    } catch (error) {
      console.error('Recalculate error:', error);
      toast.error('Error recalculating from booking');
    } finally {
      setRecalculatingPlanId(null);
    }
  };

  const getStatusBadge = (plan) => {
    const status = typeof plan === 'string' ? plan : plan?.status;
    const verified = typeof plan === 'object' && plan?.completion_method === 'manual_verified';
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'paused':
        return <Badge className="bg-yellow-100 text-yellow-800">Paused</Badge>;
      case 'completed':
        return verified
          ? <Badge className="bg-blue-100 text-blue-800" data-testid="status-completed-verified">Completed — Verified</Badge>
          : <Badge className="bg-slate-100 text-slate-600" data-testid="status-completed-unverified">Completed (unverified)</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const isOverdue = (plan) => {
    if (plan.status !== 'active' || !plan.next_payment_date) return false;
    return new Date(plan.next_payment_date) < new Date();
  };

  const activePlans = plans.filter(p => p.status === 'active');
  const duePlans = activePlans.filter(p => isOverdue(p));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Payment Plans</h2>
          <p className="text-sm text-slate-500">
            {plans.length} total plans, {activePlans.length} active, {duePlans.length} due for processing
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPlans} data-testid="refresh-plans-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Process Results */}
      {processResults && (
        <div className={`rounded-xl p-4 ${processResults.due_count === 0 ? 'bg-blue-50 border border-blue-200' : processResults.failed > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
          <div className="flex items-start gap-3">
            {processResults.due_count === 0 ? (
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            ) : processResults.failed > 0 ? (
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            ) : (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            )}
            <div>
              <p className="font-medium">{processResults.message}</p>
              {processResults.results && processResults.results.length > 0 && (
                <div className="mt-2 space-y-1">
                  {processResults.results.map((r, i) => (
                    <p key={i} className="text-sm">
                      Plan {r.plan_id}: {r.status === 'success' ? 
                        `£${r.amount_charged?.toFixed(2)} charged, new balance: £${r.new_balance?.toFixed(2)}` : 
                        `Error: ${r.error}`
                      }
                    </p>
                  ))}
                </div>
              )}
              {processResults.details && processResults.details.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium">Active plans:</p>
                  {processResults.details.map((p, i) => (
                    <p key={i} className="text-sm text-slate-600">
                      {p.plan_id}: Next payment {formatDate(p.next_payment_date)}, Balance: £{p.remaining_balance?.toFixed(2)}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Plans List */}
      {plans.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No payment plans found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Customer</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Plan Details</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Progress</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Next Payment</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map(plan => (
                  <tr key={plan.plan_id} className={`border-b border-slate-100 hover:bg-slate-50 ${isOverdue(plan) ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-500" />
                        <div>
                          <p className="font-medium text-slate-900">{plan.customer_name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">{plan.customer_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <p className="font-medium text-slate-900">£{plan.monthly_amount?.toFixed(2)}/month</p>
                        <p className="text-xs text-slate-500">Booking: {plan.booking_id}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        {(() => {
                          // Use booking totals for accurate display
                          const bookingTotal = plan.booking_total || 0;
                          const bookingAmountPaid = plan.booking_amount_paid || 0;
                          const bookingRemaining = Math.max(0, bookingTotal - bookingAmountPaid);
                          const progressPct = bookingTotal > 0 ? (bookingAmountPaid / bookingTotal) * 100 : 0;
                          const installments = plan.installments_made ?? plan.payments_made ?? 0;
                          return (
                            <>
                              <p className="text-slate-900">
                                £{bookingAmountPaid.toFixed(2)} / £{bookingTotal.toFixed(2)}
                              </p>
                              <p className="text-[11px] italic text-slate-400">
                                {installments} installment{installments !== 1 ? 's' : ''} made · ~£{bookingRemaining.toFixed(2)} remaining (verify)
                              </p>
                              <div className="w-24 h-1.5 bg-slate-200 rounded-full mt-1">
                                <div 
                                  className="h-full bg-violet-600 rounded-full" 
                                  style={{ width: `${Math.min(100, progressPct)}%` }}
                                />
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        {plan.next_payment_date ? (
                          <>
                            <p className={isOverdue(plan) ? 'text-red-600 font-medium' : 'text-slate-900'}>
                              {formatDate(plan.next_payment_date)}
                            </p>
                            {isOverdue(plan) && (
                              <p className="text-xs text-red-500">Overdue!</p>
                            )}
                          </>
                        ) : (
                          <p className="text-slate-500">N/A</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(plan)}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const canManage = plan.status === 'active' || plan.status === 'paused';
                        const isPP = plan.type === 'payment_plan';
                        const groups = [];
                        const canCollect = plan.booking_id && ['active', 'paused', 'completed'].includes(plan.status);
                        if (canCollect) {
                          groups.push({
                            label: 'Payments',
                            items: [{
                              label: 'Collect Card Payment',
                              helper: 'Charge a saved card for an amount you enter',
                              icon: <CreditCard className="w-4 h-4 text-green-600" />,
                              onClick: () => openManualCharge(plan),
                              testId: `collect-payment-${plan.plan_id || plan.share_id}`,
                            }],
                          });
                        }
                        const planItems = [];
                        if (isPP && canManage) planItems.push({
                          label: 'Edit Instalment Amount',
                          icon: <PoundSterling className="w-4 h-4 text-indigo-600" />,
                          onClick: () => openAmend(plan),
                          testId: `amend-instalment-${plan.plan_id}`,
                        });
                        if (plan.status === 'active') planItems.push({
                          label: 'Pause Plan',
                          icon: <Pause className="w-4 h-4 text-yellow-600" />,
                          onClick: () => handlePausePlan(plan.plan_id),
                          testId: `pause-plan-${plan.plan_id}`,
                        });
                        if (plan.status === 'paused') planItems.push({
                          label: 'Resume Plan',
                          icon: <Play className="w-4 h-4 text-green-600" />,
                          onClick: () => handleResumePlan(plan.plan_id),
                          testId: `resume-plan-${plan.plan_id}`,
                        });
                        if (canManage) planItems.push({
                          label: 'Mark as Completed — Verified',
                          icon: <CheckCircle className="w-4 h-4 text-blue-600" />,
                          onClick: () => openVerifyComplete(plan),
                          testId: `mark-completed-${plan.plan_id}`,
                        });
                        if (plan.status === 'completed') planItems.push({
                          label: 'Reopen Plan as Active',
                          icon: <RefreshCw className="w-4 h-4 text-amber-600" />,
                          onClick: () => openReopen(plan),
                          testId: `reopen-plan-${plan.plan_id}`,
                        });
                        if (canManage) planItems.push({
                          label: 'Cancel Plan',
                          icon: <XCircle className="w-4 h-4" />,
                          destructive: true,
                          onClick: () => handleCancelPlan(plan.plan_id),
                          testId: `cancel-plan-${plan.plan_id}`,
                          confirm: {
                            title: 'Cancel Payment Plan',
                            description: `Are you sure you want to cancel this payment plan? This cannot be undone. The remaining balance of £${plan.remaining_balance?.toFixed(2)} will still be owed.`,
                            actionLabel: 'Cancel Plan',
                            cancelLabel: 'Keep Plan',
                          },
                        });
                        if (planItems.length) groups.push({ label: 'Plan', items: planItems });
                        const historyItems = [{
                          label: 'Payment History',
                          icon: <History className="w-4 h-4 text-slate-500" />,
                          onClick: () => openHistory(plan),
                          testId: `charge-history-${plan.plan_id || plan.share_id}`,
                        }];
                        if (isPP) historyItems.push({
                          label: 'Amendment History',
                          icon: <FileClock className="w-4 h-4 text-slate-500" />,
                          onClick: () => openAmendHistory(plan),
                          testId: `amend-history-${plan.plan_id}`,
                        });
                        groups.push({ label: 'History', items: historyItems });
                        if (canManage) {
                          groups.push({
                            label: 'Advanced — maintenance only',
                            note: 'Calculated balances may be unreliable — verify against external financial records.',
                            items: [
                              {
                                label: 'Record Manual Payment',
                                helper: 'Records a payment taken elsewhere — does NOT charge a card',
                                icon: <Plus className="w-4 h-4 text-blue-600" />,
                                onClick: () => openRecordPaymentModal(plan),
                                testId: `record-payment-${plan.plan_id}`,
                              },
                              {
                                label: 'Adjust Balance',
                                icon: <Edit className="w-4 h-4 text-orange-600" />,
                                onClick: () => openAdjustBalanceModal(plan),
                                testId: `adjust-balance-${plan.plan_id}`,
                              },
                              {
                                label: 'Recalculate from Booking',
                                icon: <RotateCcw className="w-4 h-4 text-purple-600" />,
                                onClick: () => handleRecalculateFromBooking(plan.plan_id),
                                disabled: recalculatingPlanId === plan.plan_id,
                                testId: `recalculate-${plan.plan_id}`,
                                confirm: {
                                  title: 'Recalculate Balance from Booking',
                                  description: 'This recalculates the plan balance and instalment count from the booking record. Website-calculated balances may be unreliable — verify against your external financial records first. Continue?',
                                  actionLabel: 'Recalculate',
                                },
                              },
                            ],
                          });
                        }
                        return (
                          <RowActionsMenu
                            groups={groups}
                            triggerTestId={`manage-plan-${plan.plan_id || plan.share_id}`}
                          />
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      <Dialog open={recordPaymentModalOpen} onOpenChange={setRecordPaymentModalOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Manual Payment</DialogTitle>
            <DialogDescription>
              Record a payment that was processed manually via Square Dashboard or other means.
            </DialogDescription>
          </DialogHeader>
          {selectedPlan && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm font-medium">{selectedPlan.customer_name}</p>
                <p className="text-xs text-slate-500">{selectedPlan.customer_email}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Current balance: £{selectedPlan.remaining_balance?.toFixed(2)} remaining
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Payment Amount (£)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="e.g. 110.00"
                  className="mt-1"
                  data-testid="record-payment-amount"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Note (optional)</label>
                <Input
                  type="text"
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  placeholder="e.g. Payment pushed through Square dashboard"
                  className="mt-1"
                  data-testid="record-payment-note"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordPaymentModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRecordPayment} 
              disabled={submitting || !paymentAmount}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="confirm-record-payment"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Balance Modal */}
      <Dialog open={adjustBalanceModalOpen} onOpenChange={setAdjustBalanceModalOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adjust Balance</DialogTitle>
            <DialogDescription>
              Directly adjust the remaining balance and payment count. Use this to correct discrepancies.
            </DialogDescription>
          </DialogHeader>
          {selectedPlan && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm font-medium">{selectedPlan.customer_name}</p>
                <p className="text-xs text-slate-500">{selectedPlan.customer_email}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Total: £{selectedPlan.total_amount?.toFixed(2)} | 
                  Current balance: £{selectedPlan.remaining_balance?.toFixed(2)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">New Remaining Balance (£)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={adjustedBalance}
                  onChange={(e) => setAdjustedBalance(e.target.value)}
                  placeholder="e.g. 330.00"
                  className="mt-1"
                  data-testid="adjust-balance-amount"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Payments Made Count</label>
                <Input
                  type="number"
                  min="0"
                  value={adjustedPaymentsMade}
                  onChange={(e) => setAdjustedPaymentsMade(e.target.value)}
                  placeholder="e.g. 2"
                  className="mt-1"
                  data-testid="adjust-payments-made"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Reason (optional)</label>
                <Input
                  type="text"
                  value={adjustNote}
                  onChange={(e) => setAdjustNote(e.target.value)}
                  placeholder="e.g. Correcting after Square manual push"
                  className="mt-1"
                  data-testid="adjust-balance-note"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustBalanceModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAdjustBalance} 
              disabled={submitting}
              className="bg-orange-600 hover:bg-orange-500"
              data-testid="confirm-adjust-balance"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Adjust Balance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process Payment Confirmation Dialog */}
      <AlertDialog open={processConfirmOpen} onOpenChange={setProcessConfirmOpen}>
        <AlertDialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-600" />
              Confirm Payment Processing
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>You are about to charge the customer's card on file.</p>
                {planToProcess && (
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Customer:</span>
                      <span className="font-medium text-slate-900">
                        {planToProcess.customer_name || planToProcess.dancer_name || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Monthly Amount:</span>
                      <span className="font-semibold text-green-700">
                        £{(planToProcess.monthly_amount || planToProcess.monthly_payment || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Remaining Balance:</span>
                      <span className="font-medium text-slate-900">
                        £{(() => {
                          // Calculate remaining for both plan types
                          if (planToProcess.remaining_balance !== undefined) {
                            return planToProcess.remaining_balance.toFixed(2);
                          }
                          const shareAmount = planToProcess.share_amount || 0;
                          const amountPaid = planToProcess.amount_paid || 0;
                          return Math.max(0, shareAmount - amountPaid).toFixed(2);
                        })()}
                      </span>
                    </div>
                    <hr className="border-slate-200" />
                    <div className="flex justify-between text-lg">
                      <span className="font-semibold text-slate-700">Amount to Charge:</span>
                      <span className="font-bold text-green-700">
                        £{(() => {
                          const monthly = planToProcess.monthly_amount || planToProcess.monthly_payment || 0;
                          let remaining;
                          if (planToProcess.remaining_balance !== undefined) {
                            remaining = planToProcess.remaining_balance;
                          } else {
                            const shareAmount = planToProcess.share_amount || 0;
                            const amountPaid = planToProcess.amount_paid || 0;
                            remaining = Math.max(0, shareAmount - amountPaid);
                          }
                          return Math.min(monthly, remaining).toFixed(2);
                        })()}
                      </span>
                    </div>
                  </div>
                )}
                <p className="text-amber-600 text-sm font-medium">
                  ⚠️ This action will charge the customer's card immediately and cannot be undone.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPlanToProcess(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmProcessPayment}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-process-payment"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Charge Card Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Collect Card Payment (manual admin charge) */}
      <Dialog open={manualChargeOpen} onOpenChange={(o) => { if (!charging) setManualChargeOpen(o); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="manual-charge-dialog">
          <DialogHeader>
            <DialogTitle>Collect Card Payment</DialogTitle>
            <DialogDescription>
              Charge a specific amount to the customer's saved card. This does not change any booking
              balances, schedules or counters.
            </DialogDescription>
          </DialogHeader>

          {chargeResult ? (
            <div className={`rounded-lg p-4 text-sm ${chargeResult.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`} data-testid="manual-charge-result">
              {chargeResult.ok ? (
                <div>
                  <p className="font-medium flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Payment successful</p>
                  <p className="mt-1">£{Number(chargeResult.amount).toFixed(2)} charged{chargeResult.last4 ? ` to card ending ${chargeResult.last4}` : ''}.</p>
                  <p className="mt-1 text-xs break-all">Square payment: {chargeResult.payment_id}</p>
                </div>
              ) : (
                <div>
                  <p className="font-medium flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Not completed</p>
                  <p className="mt-1">{chargeResult.detail}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {manualBooking && (
                <div className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Booking</p>
                  <p className="text-sm font-medium">{manualBooking.customer_name || 'Customer'}</p>
                  <p className="text-xs text-slate-500">{manualBooking.booking_id}</p>
                  {typeof manualBooking.monthly_amount === 'number' && manualBooking.monthly_amount > 0 && (
                    <p className="text-xs text-slate-500 mt-2" data-testid="manual-charge-agreed-reference">
                      Agreed monthly instalment: <span className="font-medium text-slate-700">£{manualBooking.monthly_amount.toFixed(2)}</span>
                      <span className="text-slate-400"> — reference only; enter the amount you wish to collect below.</span>
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-700">Saved card</label>
                {loadingCards ? (
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading cards…</p>
                ) : savedCards.length === 0 ? (
                  <p className="text-sm text-red-600 mt-1">No saved cards found for this customer.</p>
                ) : (
                  <div className="mt-1 space-y-2" data-testid="manual-charge-cards">
                    {savedCards.map((c) => (
                      <label key={c.card_id} className={`flex items-center gap-2 rounded-lg border p-2 cursor-pointer ${selectedCardId === c.card_id ? 'border-green-500 bg-green-50' : 'border-slate-200'}`}>
                        <input
                          type="radio"
                          name="manual-card"
                          value={c.card_id}
                          checked={selectedCardId === c.card_id}
                          onChange={() => setSelectedCardId(c.card_id)}
                          data-testid={`manual-card-${c.card_id}`}
                        />
                        <CreditCard className="w-4 h-4 text-slate-500" />
                        <span className="text-sm">{c.card_brand || 'Card'} •••• {c.last_4}{c.exp_month ? ` (exp ${c.exp_month}/${c.exp_year})` : ''}</span>
                      </label>
                    ))}
                    {cardMerchant && <p className="text-xs text-slate-400">Merchant account: {cardMerchant}</p>}
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Amount (£)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder="e.g. 57.00"
                  data-testid="manual-charge-amount"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Note (optional)</label>
                <Input
                  value={manualNote}
                  onChange={(e) => setManualNote(e.target.value)}
                  placeholder="Reference for your records"
                  data-testid="manual-charge-note"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {chargeResult ? (
              <Button onClick={() => setManualChargeOpen(false)} data-testid="manual-charge-close">Close</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setManualChargeOpen(false)} disabled={charging}>Cancel</Button>
                <Button
                  onClick={submitManualCharge}
                  disabled={charging || loadingCards || !selectedCardId || !manualAmount}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="manual-charge-confirm"
                >
                  {charging ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Charging…</>) : (<><CreditCard className="w-4 h-4 mr-2" /> Charge £{manualAmount || '0.00'}</>)}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Read-only manual card payment history */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="charge-history-dialog">
          <DialogHeader>
            <DialogTitle>Card Payment History</DialogTitle>
            <DialogDescription>
              {historyBooking?.customer_name || 'Customer'} — {historyBooking?.booking_id}. Actual manual charge attempts only (read-only).
            </DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading history…
            </div>
          ) : historyCharges.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="charge-history-empty">
              No card payment attempts recorded for this booking.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm" data-testid="charge-history-table">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-400 border-b">
                    <th className="py-2 pr-3">Date / Time</th>
                    <th className="py-2 pr-3">Amount</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Card</th>
                    <th className="py-2 pr-3">Square Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {historyCharges.map((c, i) => (
                    <tr key={c.id || i} className="border-b last:border-0" data-testid={`charge-history-row-${i}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleString('en-GB') : '—'}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{typeof c.amount === 'number' ? `£${c.amount.toFixed(2)}` : '—'}</td>
                      <td className="py-2 pr-3">
                        <Badge
                          variant="outline"
                          className={
                            c.status === 'succeeded' ? 'bg-green-50 text-green-700 border-green-200' :
                            c.status === 'uncertain' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                            'bg-red-50 text-red-700 border-red-200'
                          }
                        >
                          {c.status === 'succeeded' ? 'Successful' : c.status === 'uncertain' ? 'Uncertain' : 'Declined'}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">{c.card_brand || 'Card'}{c.card_last4 ? ` •••• ${c.card_last4}` : ''}</td>
                      <td className="py-2 pr-3 text-xs break-all max-w-[160px]">{c.square_payment_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)} data-testid="charge-history-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Read-only instalment amendment history */}
      <Dialog open={amendHistoryOpen} onOpenChange={setAmendHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="amend-history-dialog">
          <DialogHeader>
            <DialogTitle>Instalment Amendment History</DialogTitle>
            <DialogDescription>
              {amendHistoryPlan?.customer_name || 'Customer'} — {amendHistoryPlan?.plan_id}. Who changed the monthly amount, and when (read-only).
            </DialogDescription>
          </DialogHeader>
          {amendHistoryLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading amendments…
            </div>
          ) : amendHistoryRecords.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="amend-history-empty">
              No instalment amendments recorded for this plan.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm" data-testid="amend-history-table">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-400 border-b">
                    <th className="py-2 pr-3">Date / Time</th>
                    <th className="py-2 pr-3">Administrator</th>
                    <th className="py-2 pr-3">Previous</th>
                    <th className="py-2 pr-3">New</th>
                    <th className="py-2 pr-3">Amended deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {amendHistoryRecords.map((a, i) => (
                    <tr key={a.amendment_id || i} className="border-b last:border-0" data-testid={`amend-history-row-${i}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{a.created_at ? new Date(a.created_at).toLocaleString('en-GB') : '—'}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{a.admin_email || '—'}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{typeof a.old_monthly_amount === 'number' ? `£${a.old_monthly_amount.toFixed(2)}` : '—'}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{typeof a.new_monthly_amount === 'number' ? `£${a.new_monthly_amount.toFixed(2)}` : '—'}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{a.end_date_changed && a.new_end_date ? new Date(a.new_end_date).toLocaleDateString('en-GB') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAmendHistoryOpen(false)} data-testid="amend-history-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit monthly instalment amount (future instalments only) */}
      <Dialog open={amendOpen} onOpenChange={(o) => { if (!amendSaving) setAmendOpen(o); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="amend-instalment-dialog">
          <DialogHeader>
            <DialogTitle>Edit Instalment Amount</DialogTitle>
            <DialogDescription>
              Changes the monthly amount for all future instalments only. No payment is taken now, and scheduled collection is not started.
            </DialogDescription>
          </DialogHeader>
          {amendPlan && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-sm font-medium">{amendPlan.customer_name || 'Customer'}</p>
                <p className="text-xs text-slate-500">{amendPlan.booking_id}</p>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <span className="text-sm text-slate-500">Current monthly instalment</span>
                <span className="text-sm font-semibold text-slate-900" data-testid="amend-current-amount">
                  £{Number(amendPlan.monthly_amount || 0).toFixed(2)}
                </span>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">New monthly instalment (£)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amendMonthly}
                  onChange={(e) => setAmendMonthly(e.target.value)}
                  placeholder="e.g. 50.00"
                  data-testid="amend-new-amount"
                />
                {amendMonthly && parseFloat(amendMonthly) > 0 && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="amend-preview">
                    New amount: <span className="font-medium text-slate-700">£{parseFloat(amendMonthly).toFixed(2)}</span> per instalment.
                    The final instalment will automatically settle whatever balance remains.
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Final-deadline date (optional)</label>
                <Input
                  type="date"
                  value={amendEndDate}
                  onChange={(e) => setAmendEndDate(e.target.value)}
                  data-testid="amend-end-date"
                />
                <p className="text-xs text-slate-400 mt-1">Leave blank to keep the current deadline. Balance, payments and card details are never changed.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAmendOpen(false)} disabled={amendSaving}>Cancel</Button>
            <Button onClick={submitAmend} disabled={amendSaving} data-testid="amend-save">
              {amendSaving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving…</> : 'Save new amount'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Completed — Verified */}
      <Dialog open={verifyOpen} onOpenChange={(o) => { if (!verifySaving) setVerifyOpen(o); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="verify-complete-dialog">
          <DialogHeader>
            <DialogTitle>Mark as Completed — Verified</DialogTitle>
            <DialogDescription>
              This marks the plan Completed. It changes no balances or payment history. Use this only
              after you have personally verified full payment against your authoritative external
              financial records — the website's calculated balances may be unreliable.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Do not rely on the figures shown in this tool. Confirm against your own records before verifying.</span>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                checked={verifyConfirmed}
                onChange={(e) => setVerifyConfirmed(e.target.checked)}
                data-testid="verify-confirm-checkbox"
              />
              <span>I confirm I have verified that this plan is fully paid against external financial records.</span>
            </label>
            <div>
              <label className="text-sm font-medium text-slate-700">Note (optional)</label>
              <textarea
                className="mt-1 w-full border border-slate-300 rounded-md p-2 text-sm"
                rows={2}
                value={verifyNote}
                onChange={(e) => setVerifyNote(e.target.value)}
                placeholder="e.g. Verified against bank statement / spreadsheet row 42"
                data-testid="verify-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyOpen(false)} disabled={verifySaving} data-testid="verify-complete-cancel">Cancel</Button>
            <Button
              onClick={submitVerifyComplete}
              disabled={verifySaving || !verifyConfirmed}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="verify-complete-submit"
            >
              {verifySaving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving…</> : 'Mark Completed — Verified'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen a Completed plan */}
      <Dialog open={reopenOpen} onOpenChange={(o) => { if (!reopenSaving) setReopenOpen(o); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="reopen-plan-dialog">
          <DialogHeader>
            <DialogTitle>Reopen Plan as Active</DialogTitle>
            <DialogDescription>
              This sets the plan back to Active. It does not change balances, recalculate instalments,
              create or delete payments, modify payment history, or start any automatic charging.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Reason <span className="text-red-500">*</span></label>
              <textarea
                className="mt-1 w-full border border-slate-300 rounded-md p-2 text-sm"
                rows={3}
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="e.g. Marked Completed in error — £138 still outstanding per spreadsheet"
                data-testid="reopen-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)} disabled={reopenSaving} data-testid="reopen-plan-cancel">Cancel</Button>
            <Button
              onClick={submitReopen}
              disabled={reopenSaving || !reopenReason.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="reopen-plan-submit"
            >
              {reopenSaving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Reopening…</> : 'Reopen as Active'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </div>
  );
}
